import type { AchGoal, AchievementsView } from "../model/types";

/**
 * Цели «что сделать → что получите» на экранах действий (владелец, 01.10:
 * «везде на всех экранах показывать, какие достижения можно получить, если
 * сделать то и то»). Строки пишет сервер (GET /api/achievements/app/me goals[]),
 * свои числа человека; ещё не начатое — «ступень 1».
 *
 * Модуль лёгкий — ни каталога, ни картинок: его тянет кусок подсказки.
 */

/**
 * Ключи, которые двигает действие экрана, — в порядке экрана: сначала простое
 * повседневное действие, потом лестница, потом «глубина». Совпадают с id строк
 * сервера: ключ достижения или «lvl:<сфера | лестница>».
 */
export const GOAL_KEYS = {
    /** Недели по плану: календарь тренировок, составление плана. */
    plan: ["wayfinder_map", "lvl:workouts", "unbending_will"],
    /** Тестовая тренировка на проверку: фонарь — каждая принятая, печать — одна в месяц. */
    check: ["pioneer_lantern", "masters_seal"],
    /** Любая тренировка (загрузка файлов с часов): их число, потом часы практики. */
    any: ["firebird_feather", "starlit_hours"],
    /** Своя встреча: создать, вести, позвать. */
    organise: ["banner_of_gatherings", "kind_lighthouse", "constellation"],
    /** Клуб: встречи своего клуба, месяцы в клубе, другие клубы, марафоны клуба. */
    club: ["magic_tablecloth", "deep_roots", "flying_carpet", "laurel_wreath"],
    /** Марафоны лиги. */
    marathon: ["laurel_wreath"],
    /** Добрые дела. */
    kind: ["fireflies_of_kindness"],
    /**
     * Реферальная программа (ссылка ?ref= на курс, «Пригласить друга»): только уровень
     * «Приглашённых» — его баллы идут от друзей, пришедших по этой ссылке. «Почтовый голубь»,
     * «Попутный ветер», «Открытая дверь», «Семена в полёте» сервер считает лишь по принятым
     * ссылкам умного поиска (achievements_rules/invites.py; ?ref= — фаза B): здесь они бы не
     * сдвинулись никогда.
     */
    referral: ["lvl:referrals"],
    /**
     * Организатор зовёт людей на свою встречу: «Добрый маяк» — впервые пришедший к нему.
     * «Открытой двери» нет: её двигает только друг по ссылке умного поиска, а не выбор
     * знакомых в этом окне; участнику, зовущему на чужую встречу, строки нет вовсе.
     */
    meetInvite: ["kind_lighthouse"],
    /** Уровень «Общественного» — запасная строка участника встречи. */
    socialLevel: ["lvl:social"],
    /** Умный поиск: свои вопросы, потом часы лекций и клипов, потом уровень «Обучения». */
    search: ["spyglass", "silver_saucer", "lvl:learning"],
    /** Открытый список клипов умного поиска: смотрят — сначала часы лекций и клипов. */
    searchWatch: ["silver_saucer", "spyglass", "lvl:learning"],
    /** «Моё обучение» (вкладка «Курсы»): уроки, мини-тесты, курс целиком, потом уровень «Обучения». */
    course: ["golden_key", "puzzle_casket", "unfurled_scroll", "lvl:learning"],
    /** Урок курса: следующий урок — шаг к «Золотому ключу». */
    lesson: ["golden_key"],
    /** «Часть пройдена»: курс целиком, потом уроки. */
    partDone: ["unfurled_scroll", "golden_key"],
    /** Мини-тест урока сдан: тесты, потом уроки. */
    miniTest: ["puzzle_casket", "golden_key"],
    /** Вопрос специалисту по уроку: другие отмечают «мне тоже интересно». */
    lessonQuestion: ["apt_question"],
    /**
     * Отзыв о курсе. На диете, диагностике и в «Моих отзывах» строки пока нет: сервер
     * засчитывает и отзыв о них, но пояснение каталога — «отзывы о курсах и книгах».
     */
    review: ["good_tidings"],
    /** Лекции, плеер лекции, клип: часы лекций и клипов. */
    watch: ["silver_saucer"],
    /** Правка субтитров. */
    subs: ["magic_brush"],
} as const;

/**
 * Строки целей из своего вида: goals[] сервера, а пока он их не присылает —
 * его nearest[] (там только начатое).
 */
export const goalRows = (view: AchievementsView | null | undefined): AchGoal[] => {
    if (!view?.self) return [];
    if (view.goals) return view.goals;
    return (view.nearest || []).map((g) => ({ ...g, started: true }));
};

const ordered = (rows: readonly AchGoal[], keys: readonly string[]): AchGoal[] => {
    const order = new Map<string, number>();
    keys.forEach((k, i) => {
        if (!order.has(k)) order.set(k, i);
    });
    const mine = rows.filter((r) => order.has(r.id));
    const started = mine.filter((r) => r.started);
    const fresh = mine.filter((r) => !r.started).sort((a, b) => order.get(a.id)! - order.get(b.id)!);
    return [...started, ...fresh];
};

/**
 * Цели экрана: только ключи этого экрана; начатое — первым, в порядке сервера
 * (ближе к ступени — выше); потом не начатое — в порядке экрана (ключи), чтобы
 * простое повседневное шло раньше трудного «все 5 слоёв». Не хватило строк —
 * добираются тем же порядком из запасного списка then (как forKeys приложения).
 */
export const goalsFor = (
    rows: readonly AchGoal[],
    keys: readonly string[],
    limit = 1,
    then: readonly string[] = [],
): AchGoal[] => {
    const n = Math.max(0, limit);
    const own = ordered(rows, keys).slice(0, n);
    if (own.length >= n || !then.length) return own;
    const more = ordered(rows, then).filter((r) => !own.some((o) => o.id === r.id));
    return [...own, ...more.slice(0, n - own.length)];
};

/**
 * Цели сферы (карточка сферы «Как получить баллы») или, без сферы, лучшие вообще
 * («Прогресс и лига»): в порядке сервера — начатое ближе к ступени выше, потом
 * не начатое от простого к трудному (сервер, EASE_ORDER).
 */
export const goalsOf = (rows: readonly AchGoal[], sphere: string | undefined, limit = 1): AchGoal[] =>
    rows.filter((r) => !sphere || r.sphere === sphere).slice(0, Math.max(0, limit));

/** Куда ведёт «Все достижения ›» окна строки: страница «Достижения», сразу с выбранной сферой строки. */
export const goalHref = (lang: string, row: Pick<AchGoal, "anchor">): string =>
    `/${lang}/account/achievements${row.anchor ? `#${row.anchor}` : ""}`;
